import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { EventProps } from 'react-big-calendar';
import { RBCEvent } from '../types';
import {
  Phone as PhoneIcon,
  Label as ServiceIcon
} from '@mui/icons-material';

interface EventComponentProps extends EventProps<RBCEvent> {
  disableTooltip?: boolean;
  isAgenda?: boolean;
}

export const EventComponent: React.FC<EventComponentProps> = ({ event, disableTooltip, isAgenda }) => {
  // Guard against missing event or resource
  if (!event || !event.resource) return null;
  const { resource } = event;

  const rawName = (resource.lead_name || '').trim();
  const leadPhone = resource.lead_phone;
  const service = resource.services && resource.services.length > 0 ? resource.services[0] : null;

  // If no name → show phone as the primary identifier instead of "Unknown"
  const hasName = rawName.length > 0;
  const displayName = hasName ? rawName : (leadPhone || 'No Info');

  const eventContent = isAgenda ? (
    <Box sx={{ p: 0.5, pl: 2.4, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
          fontSize: '0.95rem',
          color: 'text.primary',
          lineHeight: 1.1
        }}
      >
        {displayName}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, opacity: 0.8 }}>
        {leadPhone && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PhoneIcon sx={{ fontSize: '0.85rem' }} />
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{leadPhone}</Typography>
          </Box>
        )}
        {service && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ServiceIcon sx={{ fontSize: '0.85rem' }} />
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{service}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  ) : (
    <Box sx={{
      height: '100%',
      width: '100%',
      p: 0.4,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      minHeight: 0,
      position: 'relative',
      gap: 0.2
    }}>
      {/* Name Row */}
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          fontSize: '0.75rem',
          lineHeight: 1.1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flexShrink: 0,
          color: 'inherit'
        }}
      >
        {displayName}
      </Typography>

      {/* When name is shown, show phone below. When phone is the name, show service below. */}
      {hasName && leadPhone ? (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.3,
          opacity: 0.9,
          flexShrink: 0,
          '& .MuiSvgIcon-root': { fontSize: '0.7rem' }
        }}>
          <PhoneIcon />
          <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {leadPhone}
          </Typography>
        </Box>
      ) : !hasName && service ? (
        /* Phone is the title → show service as the subtitle */
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.3,
          opacity: 0.9,
          flexShrink: 1,
          minHeight: 0,
          overflow: 'hidden',
          '& .MuiSvgIcon-root': { fontSize: '0.7rem' }
        }}>
          <ServiceIcon />
          <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {service}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );

  if (disableTooltip) {
    return eventContent;
  }

  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{displayName}</Typography>
          {leadPhone && <Typography variant="caption" display="block">Phone: {leadPhone}</Typography>}
          {service && <Typography variant="caption" display="block">Service: {service}</Typography>}
          {resource.notes && (
            <Typography
              variant="caption"
              display="block"
              sx={{
                mt: 0.5,
                pt: 0.5,
                borderTop: '1px solid rgba(255,255,255,0.2)',
                fontStyle: 'italic'
              }}
            >
              Notes: {resource.notes}
            </Typography>
          )}
        </Box>
      }
      arrow
      placement="bottom"
    >
      {eventContent}
    </Tooltip>
  );
};
